import type { VideoComponentData } from './../ricos-content';
import { VIDEO_TYPE } from './../ricos-content';
import { unreachable } from './../ricos-content/ts-utils';
import type { PluginServices } from './../ricos-plugins/editorPlugins';
import type { PublicCommands } from './../ricos-types';
import { logInvariant } from './../_shared/log-invariant';
import type { PickMediaFn } from './../_shared/media/pick-media';
import type { PmVideoNode } from './../_shared/pm-node-types';
import { upload } from './../_shared/upload/upload';

import { RICOS_DEFAULTS } from './constants';
import type { VideoPluginEditorConfig } from './types';

const getVideoAttributes = ({
  uploadData,
  componentData,
  config,
}: {
  uploadData: VideoComponentData & { url?: string };
  componentData: PmVideoNode['attrs'] | null;
  config: Pick<VideoPluginEditorConfig, 'disableDownloadDefault'>;
}) => {
  const { pathname, thumbnail: uploadedThumbnail, url: uploadedUrl, title } = uploadData;

  const src = pathname ? { id: pathname } : { url: uploadedUrl };
  const thumbnailObject = uploadedThumbnail
    ? {
        thumbnail: {
          src: { id: uploadedThumbnail.pathname },
          width: uploadedThumbnail.width,
          height: uploadedThumbnail.height,
        },
      }
    : {};

  const titleObject = title ? { title } : {};

  return {
    video: { src },
    ...thumbnailObject,
    ...titleObject,
    disableDownload: componentData?.video?.src
      ? componentData.disableDownload
      : config.disableDownloadDefault || null,
  };
};

const getVideoPickMedia = ({
  services,
  config,
}: {
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config: Pick<
    VideoPluginEditorConfig,
    'disableDownloadDefault' | 'handleFileUpload' | 'handleFileSelection'
  >;
}): PickMediaFn => {
  const { handleFileUpload, handleFileSelection } = config;
  const devPickMedia = services.devService().pickMedia;

  return devPickMedia
    ? devPickMedia
    : handleFileSelection
    ? async () => {
        const data = await new Promise<VideoComponentData>(resolve =>
          handleFileSelection(({ data }) => resolve(data))
        );
        return { type: 'ready-media' as const, media: [data] };
      }
    : async ({ mimeType, multiple }) => {
        const files =
          (await services.uploadService?.selectFiles({ accept: mimeType, multiple })) ?? [];
        return { type: 'file' as const, file: files, customUploadFn: handleFileUpload };
      };
};

export const handleVideoUpload = async ({
  services,
  config,
  publicCommands,
  nodeId,
}: {
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config: Pick<
    VideoPluginEditorConfig,
    'disableDownloadDefault' | 'handleFileUpload' | 'handleFileSelection'
  >;
  publicCommands: PublicCommands;
  nodeId?: string;
}) => {
  const { handleFileUpload } = config;

  const pickMedia = getVideoPickMedia({ services, config });

  const result = await pickMedia({ mimeType: 'video/*', multiple: false });

  switch (result.type) {
    case 'ready-media': {
      const data = (result.media[0] as VideoComponentData) || {};
      if (nodeId) {
        const componentData: PmVideoNode['attrs'] = publicCommands?.getBlockComponentData(nodeId);

        publicCommands?.setBlock(nodeId, VIDEO_TYPE, {
          ...componentData,
          ...getVideoAttributes({ uploadData: data, componentData, config }),
        } satisfies PmVideoNode['attrs']);
        return;
      }

      queueMicrotask(() => {
        publicCommands.insertBlockWithBlankLines(
          VIDEO_TYPE,
          {
            ...getVideoAttributes({
              uploadData: data,
              componentData: null,
              config,
            }),
            ...RICOS_DEFAULTS,
          },
          {
            updateSelection: true,
          }
        );
      });
      return;
    }
    case 'file': {
      const files = result.file;

      const targetNodeId =
        nodeId ||
        publicCommands.insertBlockWithBlankLines(VIDEO_TYPE, RICOS_DEFAULTS, {
          updateSelection: true,
        });

      const file = Array.isArray(files) ? files[0] : files;
      const trackBi = services.uploadService.trackUploadBi(VIDEO_TYPE, file.size, file.name);

      try {
        const url = URL.createObjectURL(file);
        await Promise.resolve();

        publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
          ...publicCommands?.getBlockComponentData(targetNodeId),
          video: { src: { url } },
          loading: true,
          error: null,
        });

        trackBi?.start();
        const uploadFn = result.customUploadFn || handleFileUpload;

        if (!uploadFn) {
          logInvariant(Error('No upload function provided'));
          throw new Error('No upload function provided');
        }

        const uploadedFile = await upload(uploadFn, file);
        const uploadData =
          (uploadedFile.data as VideoComponentData & { url: string | undefined }) || {};

        const componentData = publicCommands?.getBlockComponentData(targetNodeId);

        publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
          ...componentData,
          ...getVideoAttributes({ uploadData, componentData, config }),
          tempData: null,
          loading: null,
          error: null,
        } satisfies PmVideoNode['attrs']);

        URL.revokeObjectURL(url as string);
        trackBi?.success();
      } catch (error: any) {
        const componentData = publicCommands?.getBlockComponentData(targetNodeId);
        publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
          ...componentData,
          error,
          loading: null,
        });

        trackBi?.error(error);
        services.uploadService.notifyError(error);
      }
      return;
    }
    case 'cancel': {
      return;
    }
    default: {
      console.error(unreachable(result));
      return;
    }
  }
};
